import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import { useUserStats } from '../context/UserStatsContext';
import { Button, AudioPlayer, FeedbackModal, PracticeCompleteModal, PracticeTimer, LoadingWhale } from '../components/common';
import { getListeningContent, TTSVoice } from '../services/api';
import { ListeningContent, SessionResult } from '../types';

// Voice options for kids
const voiceOptions: { value: TTSVoice; label: string; flag: string }[] = [
  { value: 'en-US-JennyNeural', label: 'Jenny', flag: '\u{1F1FA}\u{1F1F8}' },
  { value: 'en-US-GuyNeural', label: 'Guy', flag: '\u{1F1FA}\u{1F1F8}' },
  { value: 'en-GB-SoniaNeural', label: 'Sonia', flag: '\u{1F1EC}\u{1F1E7}' },
  { value: 'en-GB-RyanNeural', label: 'Ryan', flag: '\u{1F1EC}\u{1F1E7}' },
];

// Fallback content
const fallbackContent: Record<string, ListeningContent> = {
  basic: {
    id: 'basic-listen-1',
    audioText: 'Hello! My name is Sam. I am seven years old. I have a dog. My dog is brown. We play in the park every day.',
    complexity: 'basic',
    questions: [
      {
        question: 'What is the speaker\'s name?',
        options: ['Tom', 'Sam', 'Max', 'Ben'],
        correctIndex: 1,
      },
      {
        question: 'How old is the speaker?',
        options: ['Six', 'Seven', 'Eight', 'Nine'],
        correctIndex: 1,
      },
      {
        question: 'What pet does the speaker have?',
        options: ['A cat', 'A bird', 'A dog', 'A fish'],
        correctIndex: 2,
      },
    ],
  },
  intermediate: {
    id: 'intermediate-listen-1',
    audioText: 'Good morning everyone! Today we are going to the zoo. The bus will leave at nine o\'clock. Please bring your lunch and water. We will see lions, elephants, and monkeys. Don\'t forget to wear comfortable shoes because we will walk a lot. See you at the bus stop!',
    complexity: 'intermediate',
    questions: [
      {
        question: 'Where are they going?',
        options: ['To the park', 'To the zoo', 'To the beach', 'To school'],
        correctIndex: 1,
      },
      {
        question: 'What time does the bus leave?',
        options: ['Eight o\'clock', 'Nine o\'clock', 'Ten o\'clock', 'Eleven o\'clock'],
        correctIndex: 1,
      },
      {
        question: 'What should they bring?',
        options: ['Books', 'Lunch and water', 'Toys', 'Homework'],
        correctIndex: 1,
      },
      {
        question: 'Why should they wear comfortable shoes?',
        options: ['It\'s cold', 'They will run', 'They will walk a lot', 'It\'s raining'],
        correctIndex: 2,
      },
    ],
  },
  advanced: {
    id: 'advanced-listen-1',
    audioText: 'Welcome to the Science Museum! Today\'s special exhibit is about space exploration. The tour begins in fifteen minutes in the main hall. You will learn about the first moon landing, which happened in nineteen sixty-nine. Astronaut Neil Armstrong was the first person to walk on the moon. After the tour, you can visit the gift shop on the second floor. Please remember that photography is not allowed inside the exhibit. Enjoy your visit!',
    complexity: 'advanced',
    questions: [
      {
        question: 'What is today\'s special exhibit about?',
        options: ['Dinosaurs', 'Ocean life', 'Space exploration', 'Ancient Egypt'],
        correctIndex: 2,
      },
      {
        question: 'When does the tour begin?',
        options: ['In ten minutes', 'In fifteen minutes', 'In twenty minutes', 'Right now'],
        correctIndex: 1,
      },
      {
        question: 'When did the first moon landing happen?',
        options: ['1959', '1969', '1979', '1989'],
        correctIndex: 1,
      },
      {
        question: 'Who was the first person to walk on the moon?',
        options: ['Buzz Aldrin', 'John Glenn', 'Neil Armstrong', 'Yuri Gagarin'],
        correctIndex: 2,
      },
      {
        question: 'What is NOT allowed inside the exhibit?',
        options: ['Talking', 'Eating', 'Photography', 'Running'],
        correctIndex: 2,
      },
    ],
  },
};

const ListeningPage: React.FC = () => {
  const navigate = useNavigate();
  const { profile } = useUser();
  const { startSession, endSession, stats, loading: statsLoading } = useUserStats();

  const [content, setContent] = useState<ListeningContent | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [answers, setAnswers] = useState<number[]>([]);
  const [isChecking, setIsChecking] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [score, setScore] = useState(0);
  const [hasListened, setHasListened] = useState(false);
  const [showQuestions, setShowQuestions] = useState(false);
  const [exerciseCount, setExerciseCount] = useState(0);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [sessionResult, setSessionResult] = useState<SessionResult | null>(null);
  const [isPracticing, setIsPracticing] = useState(true);
  const [selectedVoice, setSelectedVoice] = useState<TTSVoice>('en-US-JennyNeural');

  const complexity = profile?.complexity || 'basic';

  // Start session when stats are loaded
  useEffect(() => {
    if (stats && !statsLoading) {
      startSession('listening');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stats, statsLoading]);

  const loadContent = async () => {
    setIsLoading(true);
    setAnswers([]);
    setScore(0);
    setHasListened(false);
    setShowQuestions(false);

    try {
      const data = await getListeningContent(complexity);
      setContent(data);
    } catch (err) {
      // Use fallback
      setContent(fallbackContent[complexity]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadContent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [complexity]);

  const handleAnswerSelect = (questionIndex: number, optionIndex: number) => {
    const newAnswers = [...answers];
    newAnswers[questionIndex] = optionIndex;
    setAnswers(newAnswers);
  };

  const handleCheck = async () => {
    if (!content || !allAnswered) return;

    setIsChecking(true);

    // Calculate score locally (more reliable than API for kids app)
    let correct = 0;
    content.questions.forEach((q, i) => {
      console.log(`[DEBUG] Question ${i}: user answered ${answers[i]}, correct is ${q.correctIndex}`);
      if (answers[i] === q.correctIndex) correct++;
    });
    const calculatedScore = Math.round((correct / content.questions.length) * 100);
    console.log(`[DEBUG] Score: ${correct}/${content.questions.length} = ${calculatedScore}%`);

    setScore(calculatedScore);
    setIsChecking(false);
    setShowFeedback(true);
    setExerciseCount((prev) => prev + 1);
  };

  const handleNext = () => {
    setShowFeedback(false);
    loadContent();
  };

  const handleTryAgain = () => {
    setShowFeedback(false);
    setAnswers([]);
    setScore(0);
  };

  const handleAudioComplete = () => {
    setHasListened(true);
  };

  const handleShowQuestions = () => {
    setShowQuestions(true);
  };

  const handleGoBack = async () => {
    setIsPracticing(false);
    const sessionRes = await endSession(score);
    if (sessionRes) {
      setSessionResult(sessionRes);
      setShowProgressModal(true);
    } else {
      navigate('/app');
    }
  };

  const handleContinuePractice = () => {
    setShowProgressModal(false);
    setSessionResult(null);
    setIsPracticing(true);
    startSession('listening');
  };

  const handleGoHome = () => {
    setShowProgressModal(false);
    navigate('/app');
  };

  const allAnswered = content ? answers.length === content.questions.length && !answers.includes(undefined as any) : false;

  return (
    <div className="min-h-screen bg-kids-bg dark:bg-slate-900">
      {/* Header - Always rendered to keep timer running */}
      <header className="bg-white dark:bg-slate-800 shadow-md dark:shadow-slate-900 sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
          <button
            onClick={handleGoBack}
            className="flex items-center gap-2 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white"
          >
            <span className="text-2xl">{'\u2190'}</span>
            <span className="font-bold">Done</span>
          </button>
          <h1 className="text-2xl font-bold text-amber-600 dark:text-amber-400 flex items-center gap-2">
            <span>{'\u{1F3A7}'}</span> Listening
          </h1>
          <PracticeTimer isActive={isPracticing} />
        </div>
      </header>

      {/* Loading state */}
      {isLoading || !content ? (
        <LoadingWhale message="Loading exercise..." />
      ) : (
      /* Main content */
      <main className="max-w-2xl mx-auto px-4 py-8">
        {/* Exercise counter */}
        <div className="text-center mb-4">
          <span className="text-gray-500 dark:text-gray-400">Exercise {exerciseCount + 1}</span>
        </div>

        {/* Audio section */}
        <div className="bg-white dark:bg-slate-800 rounded-3xl p-8 shadow-lg dark:shadow-slate-900 mb-6">
          <div className="text-center mb-6">
            <span className="text-6xl mb-4 block animate-bounce-slow">{'\u{1F3A7}'}</span>
            <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-2">Listen Carefully!</h2>
            <p className="text-gray-600 dark:text-gray-300">
              {showQuestions
                ? 'You can listen again while answering!'
                : 'Listen to the audio, then answer the questions.'}
            </p>
          </div>

          {/* Voice selector */}
          <div className="flex justify-center gap-2 mb-6 flex-wrap">
            {voiceOptions.map((voice) => (
              <button
                key={voice.value}
                onClick={() => setSelectedVoice(voice.value)}
                className={`
                  px-4 py-2 rounded-xl font-medium transition-all duration-200
                  ${selectedVoice === voice.value
                    ? 'bg-amber-500 text-white shadow-md scale-105'
                    : 'bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-200 hover:bg-amber-100 dark:hover:bg-amber-900/30'
                  }
                `}
              >
                <span className="mr-1">{voice.flag}</span>
                {voice.label}
              </button>
            ))}
          </div>

          <div className="flex justify-center">
            <AudioPlayer
              key={`${content.audioText}-${selectedVoice}`}
              text={content.audioText}
              voice={selectedVoice}
              maxPlays={5}
              onComplete={handleAudioComplete}
              size="large"
            />
          </div>

          {hasListened && !showQuestions && (
            <div className="mt-6 text-center">
              <Button onClick={handleShowQuestions} variant="success" size="large">
                Show Questions
              </Button>
            </div>
          )}
        </div>

        {/* Questions section */}
        {showQuestions && (
          <div className="bg-white dark:bg-slate-800 rounded-3xl p-8 shadow-lg dark:shadow-slate-900 animate-pop">
            <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-6 text-center">
              Answer the Questions
            </h3>

            <div className="space-y-6">
              {content.questions.map((question, qIndex) => (
                <div key={qIndex} className="border-b-2 border-gray-100 dark:border-slate-700 pb-6 last:border-0">
                  <p className="font-bold text-gray-800 dark:text-white mb-4 text-lg">
                    {qIndex + 1}. {question.question}
                  </p>
                  <div className="space-y-3">
                    {question.options.map((option, oIndex) => {
                      const letters = ['A', 'B', 'C', 'D'];
                      const isSelected = answers[qIndex] === oIndex;
                      return (
                        <button
                          key={oIndex}
                          onClick={() => handleAnswerSelect(qIndex, oIndex)}
                          className={`
                            w-full p-4 rounded-2xl border-3 text-left transition-all duration-200 flex items-center gap-4
                            ${isSelected
                              ? 'border-amber-500 bg-amber-100 dark:bg-amber-900/40 shadow-md'
                              : 'border-gray-200 dark:border-slate-600 bg-gray-50 dark:bg-slate-700 hover:border-amber-300 dark:hover:border-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20'
                            }
                          `}
                        >
                          <span className={`
                            w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg flex-shrink-0
                            ${isSelected
                              ? 'bg-amber-500 text-white'
                              : 'bg-white dark:bg-slate-600 border-2 border-gray-300 dark:border-slate-500 text-gray-600 dark:text-gray-200'
                            }
                          `}>
                            {letters[oIndex]}
                          </span>
                          <span className={`text-lg ${isSelected ? 'font-bold text-amber-800 dark:text-amber-300' : 'text-gray-700 dark:text-gray-200'}`}>
                            {option}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6">
              <Button
                onClick={handleCheck}
                variant="warning"
                size="large"
                fullWidth
                disabled={!allAnswered || isChecking}
              >
                {isChecking ? 'Checking...' : 'Check Answers'}
              </Button>
            </div>
          </div>
        )}

        {/* Skip button */}
        <div className="text-center mt-6">
          <Button onClick={handleNext} variant="secondary" size="medium">
            Skip this one
          </Button>
        </div>
      </main>
      )}

      {/* Feedback Modal */}
      <FeedbackModal
        isOpen={showFeedback}
        onClose={() => setShowFeedback(false)}
        type={score >= 70 ? 'success' : 'error'}
        title={score === 100 ? 'Perfect!' : score >= 70 ? 'Great Listening!' : 'Try Again!'}
        message={
          score === 100
            ? 'You heard everything perfectly!'
            : score >= 70
            ? 'Good job! Let\'s see the answers:'
            : 'Let\'s see what you missed:'
        }
        score={score}
        buttonText="Next Exercise"
        onButtonClick={handleNext}
        quizResult={score < 100 && content ? { questions: content.questions, userAnswers: answers } : undefined}
        onTryAgain={handleTryAgain}
      />

      {/* Practice Complete Modal */}
      <PracticeCompleteModal
        isOpen={showProgressModal}
        onClose={() => setShowProgressModal(false)}
        result={sessionResult}
        onContinue={handleContinuePractice}
        onGoHome={handleGoHome}
      />
    </div>
  );
};

export default ListeningPage;
